import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { QueryState } from '@/components/feedback/query-state';
import { SkeletonTable } from '@/components/feedback/skeletons';
import { PageHeader } from '@/components/page-header';
import { AddPartner } from '@/components/partners/add-partner';
import {
  PRODUCT_LABEL,
  STATUS_LABEL,
} from '@/components/partners/partner-labels';
import { StatusPill } from '@/components/primitives/status-pill';
import { adminQuery } from '@/lib/query';

export const Route = createFileRoute('/_app/partners/')({
  component: PartnersPage,
});

/** The banks and aggregators that call the partner API. Super admin only. */
function PartnersPage() {
  const partners = useQuery(adminQuery('partners.list', {}));
  return (
    <>
      <PageHeader
        title="Partners"
        description="Banks and aggregators that call Sao's partner API: what they may do, where from, and their credentials."
        actions={<AddPartner />}
      />
      <QueryState
        query={partners}
        label="the partners"
        skeleton={<SkeletonTable columns={4} />}
      >
        {(data) =>
          data.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No partner yet. Add one to start an integration.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {data.rows.map((partner) => (
                <li key={partner.id}>
                  <Link
                    to="/partners/$partnerId"
                    params={{ partnerId: partner.id }}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 p-4 hover:bg-muted/50 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1.6fr)_8rem_auto]"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">{partner.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {partner.code}
                      </div>
                    </div>
                    <div className="hidden min-w-0 truncate text-sm text-muted-foreground sm:block">
                      {partner.products.length === 0
                        ? 'No products'
                        : partner.products
                            .map((item) => PRODUCT_LABEL[item])
                            .join(', ')}
                    </div>
                    <div className="hidden text-sm tabular-nums text-muted-foreground sm:block">
                      {partner.calls_24h} calls today
                    </div>
                    <StatusPill
                      tone={
                        partner.status === 'live'
                          ? 'success'
                          : partner.status === 'suspended'
                            ? 'danger'
                            : 'neutral'
                      }
                      label={STATUS_LABEL[partner.status]}
                    />
                  </Link>
                </li>
              ))}
            </ul>
          )
        }
      </QueryState>
    </>
  );
}
